import { api } from './api'
import { renderShareCard, type ShareCardData } from './shareCard'
import { supports, tg } from './telegram'

export type ShareOutcome = 'message' | 'story' | 'download' | 'failed'

/**
 * Делится карточкой расклада картинкой.
 *
 * Картинка обязана лежать по публичному адресу: и «поделиться в чат», и
 * «в историю» Telegram скачивает сам, data:-ссылку он не примет. Поэтому
 * порядок такой: нарисовали → загрузили на свой сервер → отдали Telegram ссылку.
 *
 * Способы по убыванию удобства:
 *  1. shareMessage — системный выбор чата (Bot API 8.0+);
 *  2. shareToStory — публикация в историю (7.8+);
 *  3. скачивание файла — вне Telegram и на старых клиентах.
 */
export async function shareCard(data: ShareCardData, caption: string, lang: string): Promise<ShareOutcome> {
  const image = await renderShareCard(data)

  // Вне Telegram грузить картинку на сервер незачем — сразу отдаём файл.
  if (!tg) {
    downloadBlob(image, fileName(data.title))
    return 'download'
  }

  let uploaded: { id: string; url: string; invite: string }
  try {
    uploaded = await api.uploadShareCard(image, lang)
  } catch {
    downloadBlob(image, fileName(data.title))
    return 'download'
  }

  if (supports('8.0')) {
    try {
      const { preparedMessageId } = await api.shareMessage({
        id: uploaded.id,
        title: data.title,
        caption,
        lang,
      })
      tg.shareMessage(preparedMessageId)
      return 'message'
    } catch {
      // Бот не настроен или API отказал — пробуем историю.
    }
  }

  if (supports('7.8')) {
    try {
      // В историю ссылку дописываем сами: подпись к ней собирает клиент, а
      // приглашение с реферальным кодом приходит с сервера вместе с адресом
      // картинки. Без него история — самая заметная витрина — не приводит
      // никого, и поделившийся ничего не получает.
      const story = uploaded.invite ? `${caption}\n\n${uploaded.invite}` : caption
      tg.shareToStory(uploaded.url, { text: story.slice(0, 200) })
      return 'story'
    } catch {
      /* старый клиент — уходим на скачивание */
    }
  }

  if (supports('6.9')) {
    tg.downloadFile({ url: uploaded.url, file_name: fileName(data.title) })
    return 'download'
  }

  return 'failed'
}

function fileName(title: string): string {
  const slug = title.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, '-').replace(/^-|-$/g, '')
  return `sirr-${slug || 'card'}.jpg`
}

function downloadBlob(blob: Blob, name: string): void {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = name
  document.body.appendChild(a)
  a.click()
  a.remove()
  // Отзываем ссылку не сразу: Safari успевает не начать скачивание.
  setTimeout(() => URL.revokeObjectURL(url), 10000)
}
